import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { generalSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useEditProviderMutation,
  usePartnerShortQuery,
  usePracticeShortQuery,
  useProviderClinicalShortQuery,
  useProviderRegionShortQuery,
} from '@common/cache';
import { paths, ProviderSpecialtyType } from '@common/types';
import { useContext, useEffect, useState } from 'react';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';

type SelectOption = { value: string; label: string };

const mergeSelectedOption = (
  options: SelectOption[],
  selected?: { id: string; name: string } | null,
): SelectOption[] => {
  if (!selected) {
    return options;
  }

  if (options.some(o => o.value === selected.id)) {
    return options;
  }

  return [{ value: selected.id, label: selected.name }, ...options];
};

const providerSpecialtyOptions: ProviderSpecialtyType[] = [
  'Cardiology',
  'Palliative care',
  'Primary care',
  'Neurology',
  'Geropsych',
  'Home Health',
  'Social Worker',
];

interface IGeneralFormShape {
  first_name: string;
  last_name: string;
  partner_id: string;
  practice_id: string;
  provider_specialty: string;
  inactive: boolean;
  external: boolean;
  suffix?: string;
  email?: string | null;
  phone_number?: string | null;
  slack_link?: string;
  region_id?: string | null;
  clinical_leader_id?: string | null;
  supervising_physician_id?: string | null;
}

const initialValues: Partial<IGeneralFormShape> = {
  first_name: '',
  last_name: '',
  suffix: '',
  external: false,
  partner_id: '',
  practice_id: '',
  email: '',
  phone_number: '',
  provider_specialty: '',
  slack_link: '',
  inactive: false,
  region_id: null,
  clinical_leader_id: null,
  supervising_physician_id: null,
};

export const useGeneral = (
  provider: paths[`/provider/{record_id}`]['get']['responses']['200']['content']['application/json'],
) => {
  const {
    triggerClose,
    triggerTabChange,
    setIsFormClean,
    handleCloseConfirmationModal,
  } = useContext(AdminPanelDrawerContext);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const [regionSearchQuery, setRegionSearchQuery] = useState(
    provider.region?.name || '',
  );
  const [clinicalSearchQuery, setClinicalSearchQuery] = useState(
    provider.clinical_leader?.name || '',
  );
  const { data: partnerOptions = [] } = usePartnerShortQuery();
  const { mutateAsync, isPending } = useEditProviderMutation(provider.id);

  const {
    data: providerRegionOptions = [],
    isLoading: isRegionOptionsLoading,
  } = useProviderRegionShortQuery(regionSearchQuery || undefined);

  const {
    data: providerClinicalOptions = [],
    isLoading: isClinicalOptionsLoading,
  } = useProviderClinicalShortQuery(clinicalSearchQuery || undefined);

  const formMethods = useForm<IGeneralFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IGeneralFormShape, any, any>(generalSchema),
    defaultValues: {
      ...initialValues,
      ...provider,
      practice_id: provider.practice.id,
      partner_id: provider.partner.id,
      region_id: provider.region?.id ?? null,
      clinical_leader_id: provider.clinical_leader?.id ?? null,
      supervising_physician_id: provider.supervising_physician?.id ?? null,
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty, errors, isValid, dirtyFields },
    setValue,
    reset,
    getValues,
  } = formMethods;

  const part_id = useWatch({
    control: formMethods.control,
    name: 'partner_id',
  });

  const { data: practiceOptions = [], refetch } = usePracticeShortQuery({
    record_id: part_id,
  });

  useEffect(() => {
    if (part_id) {
      refetch();
    }
  }, [part_id]);

  useEffect(() => {
    if (triggerClose && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (triggerTabChange && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (isDirty && Object.keys(dirtyFields).length > 0) {
      setIsFormClean(false);
      return;
    }

    if (triggerClose && !isDirty) {
      setIsFormClean(true);
    }

    if (triggerTabChange && !isDirty) {
      setIsFormClean(true);
    }
  }, [triggerClose, isDirty, triggerTabChange]);

  const handleChangeRegionQuery = (value: string) => {
    setRegionSearchQuery(value);
  };

  const handleChangeClinicalQuery = (value: string) => {
    setClinicalSearchQuery(value);
  };

  const submitHandler = async (values: IGeneralFormShape) => {
    try {
      await mutateAsync({
        ...provider,
        ...values,
        provider_id: provider.id,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        provider_specialty: values.provider_specialty as any,
        slack_link: values.slack_link || null,
        email: values.email || null,
        record_id: values.practice_id,
        region_id: values.region_id || null,
        clinical_leader_id: values.clinical_leader_id || null,
        supervising_physician_id: values.supervising_physician_id || null,
      });

      toast.success('Changes saved');
      reset(values);
      setIsFormClean(true);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleCloseConfirmationModal();
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormClean(true);
  };

  const handleSaveChanges = async () => {
    await onSubmit();
    reset(getValues());
  };

  return {
    onSubmit,
    isSubmitEnabled:
      isDirty && Object.keys(errors).length === 0 && !isSubmitting,
    formMethods,
    practiceOptions: practiceOptions.map(o => ({ value: o.id, label: o.name })),
    partnerOptions: partnerOptions.map(o => ({ value: o.id, label: o.name })),
    providerSpecialtyOptions: providerSpecialtyOptions.map(o => ({
      value: o,
      label: o,
    })),
    providerRegionOptions: mergeSelectedOption(
      providerRegionOptions.map(o => ({
        value: o.id,
        label: o.name,
      })),
      provider.region,
    ),
    providerClinicalOptions: mergeSelectedOption(
      providerClinicalOptions.map(o => ({
        value: o.id,
        label: o.name,
      })),
      provider.clinical_leader,
    ),
    setValue,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
    regionSearchQuery,
    isRegionOptionsLoading,
    clinicalSearchQuery,
    isClinicalOptionsLoading,
    handleChangeRegionQuery,
    handleChangeClinicalQuery,
  };
};
